import { auth, db } from "@/lib/firebase";
import {
  getPioneerSlotOnboardingDisposition,
  type PioneerSlotOnboardingDisposition,
} from "@/lib/pioneerSlotDecision";
import type { PioneerSlot, PioneerSlotStatus } from "@/types/pioneerSlot";
import type { User } from "firebase/auth";
import {
  doc,
  getDoc,
} from "firebase/firestore";

export {
  getPioneerSlotOnboardingDisposition,
  type PioneerSlotOnboardingDisposition,
};

export const PIONEER_SLOTS_COLLECTION = "pioneerSlots";

export const PIONEER_PROGRAM_SETTINGS_COLLECTION = "appSettings";
export const PIONEER_PROGRAM_SETTINGS_DOC = "pioneerProgram";

type CreatePioneerSlotInput = {
  ownerName: string;
  businessName: string;
  mobileNumber: string;
};

export type PioneerProgramSettings = {
  isOpen: boolean;
  limit: number;
  claimedCount: number;
  isConfigured: boolean;
  hasCapacity: boolean;
};

type SignedInGoogleUser = User & {
  email: string;
};

function cleanText(value: unknown, fallback = ""): string {
  if (typeof value !== "string") return fallback;
  return value.trim();
}

function cleanPhone(value: unknown): string {
  const phone = cleanText(value);
  return phone.replace(/\s+/g, " ");
}

function cleanStatus(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function readNumber(
  data: Record<string, unknown>,
  keys: string[],
  fallback = 0
): number {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return fallback;
}

function readBusinessReference(
  data: Record<string, unknown>
): { businessId: string | null; malformed: boolean } {
  if (!Object.prototype.hasOwnProperty.call(data, "businessId")) {
    return { businessId: null, malformed: false };
  }

  const raw = data.businessId;
  if (raw === "") return { businessId: null, malformed: false };
  if (
    typeof raw !== "string" ||
    raw.trim() !== raw ||
    !raw ||
    raw.length > 1_500 ||
    raw.includes("/")
  ) {
    return { businessId: null, malformed: true };
  }

  return { businessId: raw, malformed: false };
}

function isGoogleSignedIn(user: User | null): boolean {
  return (
    !!user &&
    user.providerData.some(
      (provider) => provider.providerId === "google.com"
    )
  );
}

function requireSignedInGoogleUser(): SignedInGoogleUser {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("You must be signed in.");
  }

  if (!user.email) {
    throw new Error("Your Google account email could not be found.");
  }

  if (!user.emailVerified) {
    throw new Error("Please use a verified Google account email.");
  }

  if (!isGoogleSignedIn(user)) {
    throw new Error("Please sign in with Google to continue.");
  }

  return user as SignedInGoogleUser;
}

export function normalizePioneerSlotStatus(
  data: Record<string, unknown>
): PioneerSlotStatus {
  const hasExplicitStatus = Object.prototype.hasOwnProperty.call(
    data,
    "status"
  );
  const status = cleanStatus(data.status);

  if (
    status === "available" ||
    status === "active" ||
    status === "used" ||
    status === "expired" ||
    status === "cancelled"
  ) {
    return status;
  }

  const reference = readBusinessReference(data);

  // Only an absent legacy field uses the historical businessId fallback.
  // Every explicit unrecognized value fails closed.
  if (hasExplicitStatus) {
    return "unknown";
  }

  if (reference.malformed) return "unknown";

  if (reference.businessId) {
    return "used";
  }

  return "available";
}

function toPioneerSlot(id: string, data: Record<string, unknown>): PioneerSlot {
  const ownerEmail =
    cleanText(data.ownerEmail) || cleanText(data.email) || "";

  const reference = readBusinessReference(data);
  const businessId = reference.businessId;

  const maxBusinesses = readNumber(
    data,
    ["maxBusinesses", "businessLimit", "maxListings", "listingLimit"],
    1
  );

  const usedBusinesses = readNumber(
    data,
    [
      "usedBusinesses",
      "usedBusinessCount",
      "businessesUsed",
      "usedListings",
    ],
    businessId ? 1 : 0
  );

  return {
    id,

    ownerId: cleanText(data.ownerId) || cleanText(data.uid) || id,
    uid: cleanText(data.uid) || undefined,

    ownerEmail,
    email: cleanText(data.email) || ownerEmail,
    emailLower:
      cleanText(data.emailLower) || (ownerEmail ? ownerEmail.toLowerCase() : ""),

    ownerName: cleanText(data.ownerName),
    businessName: cleanText(data.businessName),
    mobileNumber: cleanText(data.mobileNumber),

    status: normalizePioneerSlotStatus(data),

    maxBusinesses,
    usedBusinesses,

    businessId,
    businessReferenceMalformed: reference.malformed || undefined,

    firstYearFree: data.firstYearFree === false ? false : true,
    source: cleanText(data.source) || "pioneer_first_100",

    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    usedAt: data.usedAt,
    startedAt: data.startedAt,
    subscriptionStartedAt: data.subscriptionStartedAt,
    expiresAt: data.expiresAt,
    validUntil: data.validUntil,
    endsAt: data.endsAt,
    subscriptionExpiresAt: data.subscriptionExpiresAt,
  };
}

function toProgramSettings(
  data: Record<string, unknown>
): PioneerProgramSettings {
  const limitIsValid =
    typeof data.limit === "number" &&
    Number.isInteger(data.limit) &&
    data.limit > 0;
  const claimedCountIsValid =
    typeof data.claimedCount === "number" &&
    Number.isInteger(data.claimedCount) &&
    data.claimedCount >= 0;
  const openFlagIsValid = typeof data.isOpen === "boolean";
  const isConfigured =
    openFlagIsValid && limitIsValid && claimedCountIsValid;
  const isOpen = data.isOpen === true;
  const limit = limitIsValid ? (data.limit as number) : 0;
  const claimedCount = claimedCountIsValid
    ? (data.claimedCount as number)
    : 0;

  return {
    isOpen,
    limit,
    claimedCount,
    isConfigured,
    hasCapacity:
      isConfigured && isOpen && claimedCount < limit,
  };
}

export function isUsablePioneerSlot(
  slot: PioneerSlot | null | undefined,
  nowMs = Date.now()
): boolean {
  return getPioneerSlotOnboardingDisposition(slot, nowMs) === "usable";
}

export async function getPioneerProgramSettings(): Promise<PioneerProgramSettings | null> {
  const refSettings = doc(
    db,
    PIONEER_PROGRAM_SETTINGS_COLLECTION,
    PIONEER_PROGRAM_SETTINGS_DOC
  );

  const snap = await getDoc(refSettings);

  if (!snap.exists()) {
    return null;
  }

  return toProgramSettings(snap.data() as Record<string, unknown>);
}

export async function getOwnerPioneerSlot(
  ownerId: string
): Promise<PioneerSlot | null> {
  const cleanOwnerId = cleanText(ownerId);

  if (!cleanOwnerId) {
    return null;
  }

  const refSlot = doc(db, PIONEER_SLOTS_COLLECTION, cleanOwnerId);
  const snap = await getDoc(refSlot);

  if (!snap.exists()) {
    return null;
  }

  return toPioneerSlot(snap.id, snap.data() as Record<string, unknown>);
}

export async function createOrGetPioneerSlot(
  input: CreatePioneerSlotInput
): Promise<PioneerSlot> {
  const user = requireSignedInGoogleUser();

  const ownerName = cleanText(input.ownerName);
  const businessName = cleanText(input.businessName);
  const mobileNumber = cleanPhone(input.mobileNumber);

  if (ownerName.length < 2) {
    throw new Error("Please enter your name.");
  }

  if (businessName.length < 2) {
    throw new Error("Please enter your business name.");
  }

  if (mobileNumber.length < 7) {
    throw new Error("Please enter a valid mobile number.");
  }

  const refSlot = doc(db, PIONEER_SLOTS_COLLECTION, user.uid);
  const slotSnap = await getDoc(refSlot);

  if (slotSnap.exists()) {
    return toPioneerSlot(
      slotSnap.id,
      slotSnap.data() as Record<string, unknown>
    );
  }

  // Retained for compatibility with older callers. New Pioneer enrollment is
  // closed; no client path may create a new slot or increment its counter.
  throw new Error("New Pioneer enrollment is closed.");
}
